import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {apiFetch} from "../../shared/api";
import { apiErrorMessage } from "../../shared/apiErrors";

export const searchProductsByBarcodePartial = createAsyncThunk(
  "onlineSeller/searchProductsByBarcodePartial",
  async ({ barcodePartial, storeId }, { rejectWithValue }) => {
    const code = String(barcodePartial || "").trim();
    if (!code) return rejectWithValue({ barcodePartial: ["Укажите часть штрихкода"] });
    
    if (!storeId || isNaN(Number(storeId)) || Number(storeId) <= 0) {
      return rejectWithValue({ detail: "Не выбран магазин" });
    }

    const url = `/api/product/products/check-inventory-by-barcode/?barcode=${encodeURIComponent(code)}&store_id=${Number(storeId)}`;
    
    try {
      const data = await apiFetch(url, { method: "GET" });
      return Array.isArray(data) ? data : [data];
    } catch (err) {
      const errorMessage = apiErrorMessage(err, "Ошибка при поиске товара");

      return rejectWithValue({ detail: errorMessage });
    }
  }
);

export const checkCurrentShift = createAsyncThunk(
  "onlineSeller/checkCurrentShift",
  async (_, { rejectWithValue }) => {
    try {
      return await apiFetch("/api/auth/shifts/current/", {method: "GET"});
    } catch (err) {
      if (err?.status === 404) {
        return null;
      }
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to check shift" });
    }
  }
);

export const openShift = createAsyncThunk(
  "onlineSeller/openShift",
  async ({ storeId, openingAmount = "0", notes = "" }, { rejectWithValue }) => {
    const storeIdNum = Number(storeId);
    if (!storeId || isNaN(storeIdNum) || storeIdNum <= 0) {
      return rejectWithValue({ detail: "Store ID is required and must be a valid positive number" });
    }

    const requestBody = {
      store: storeIdNum,
      opening_amount: String(openingAmount || "0"),
      notes: String(notes || ""),
    };

    try {
      return await apiFetch("/api/auth/shifts/open/", {
        method: "POST",
        body: requestBody,
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to open shift" });
    }
  }
);

export const closeShift = createAsyncThunk(
  "onlineSeller/closeShift",
  async ({ storeId, closingAmount = "0", notes = "" }, { rejectWithValue }) => {
    const storeIdNum = Number(storeId);
    if (!storeId || isNaN(storeIdNum) || storeIdNum <= 0) {
      return rejectWithValue({ detail: "Store ID is required and must be a valid positive number" });
    }

    const requestBody = {
      store: storeIdNum,
      closing_amount: String(closingAmount || "0"),
      notes: String(notes || ""),
    };

    try {
      return await apiFetch("/api/auth/shifts/close/", {
        method: "POST",
        body: requestBody,
      });
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to close shift" });
    }
  }
);

export const fetchStores = createAsyncThunk(
  "onlineSeller/fetchStores",
  async (_, { rejectWithValue }) => {
    try {
      const data = await apiFetch("/api/product/stores/?is_only_warehouse=true", { method: "GET" });
      return Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
    } catch (err) {
      return rejectWithValue(err?.data || { detail: err?.message || "Failed to fetch stores" });
    }
  }
);

export const createTransaction = createAsyncThunk(
  "onlineSeller/createTransaction",
  async ({ storeId, receipt, discountTotal, paymentType, products }, { rejectWithValue }) => {
    const storeIdNum = Number(storeId);
    if (!storeId || isNaN(storeIdNum) || storeIdNum <= 0) {
      return rejectWithValue({ detail: "Store ID is required and must be a valid positive number" });
    }

    if (!receipt || String(receipt).trim() === "") {
      return rejectWithValue({ detail: "Receipt number is required" });
    }

    if (!Array.isArray(products) || products.length === 0) {
      return rejectWithValue({ detail: "At least one product is required" });
    }

    const requestBody = {
      store_id: storeIdNum,
      receipt: String(receipt),
      discount_total: String(discountTotal || "0.00"),
      payment_type: String(paymentType || "online"),
      products: products,
    };

    try {
      return await apiFetch("/api/product/transactions/", {
        method: "POST",
        body: requestBody,
      })
    } catch (err) {
      const errorData = err?.data || {};
      let errorMessage = "Ошибка при создании транзакции";
      
      if (typeof errorData === "string") {
        errorMessage = errorData;
      } else if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (err?.message) {
        errorMessage = err.message;
      } else if (typeof errorData === "object" && Object.keys(errorData).length > 0) {
        const fieldErrors = Object.entries(errorData)
          .map(([field, messages]) => {
            const msg = Array.isArray(messages) ? messages.join(", ") : String(messages);
            return `${field}: ${msg}`;
          })
          .join("; ");
        errorMessage = fieldErrors || errorMessage;
      }
      
      return rejectWithValue(errorMessage);
    }
  }
);

const onlineSellerSlice = createSlice({
  name: "onlineSeller",
  initialState: {
    searchResults: [],
    searchStatus: "idle",
    searchError: null,
    lastSearchQuery: "",
    selectedProducts: [],
    stores: {
      list: [],
      status: "idle",
      error: null,
    },
    selectedStore: null,
    transaction: {
      status: "idle",
      error: null,
      data: null,
    },
    lastReceiptNumber: 0,
    shift: {
      current: null,
      status: "idle",
      error: null,
    },
  },
  reducers: {
    clearSearchResults(state) {
      state.searchResults = [];
      state.searchStatus = "idle";
      state.searchError = null;
      state.lastSearchQuery = "";
    },
    addProductToCart(state, action) {
      const product = action.payload;
      const existingIndex = state.selectedProducts.findIndex((item) => item.product.id === product.id);
      if (existingIndex >= 0) {
        state.selectedProducts[existingIndex].quantity += 1;
      } else {
        state.selectedProducts.push({ product, quantity: 1, discount: 0 });
      }
    },
    updateProductQuantity(state, action) {
      const { productId, quantity } = action.payload;
      const item = state.selectedProducts.find((item) => item.product.id === productId);
      if (item) {
        item.quantity = Math.max(1, Math.floor(Number(quantity) || 1));
      }
    },
    incrementProductQuantity(state, action) {
      const productId = action.payload;
      const item = state.selectedProducts.find((item) => item.product.id === productId);
      if (item) {
        item.quantity += 1;
      }
    },
    decrementProductQuantity(state, action) {
      const productId = action.payload;
      const item = state.selectedProducts.find((item) => item.product.id === productId);
      if (item) {
        item.quantity = Math.max(1, item.quantity - 1);
      }
    },
    updateProductDiscount(state, action) {
      const { productId, discount } = action.payload;
      const item = state.selectedProducts.find((item) => item.product.id === productId);
      if (item) {
        const value = String(discount || "").trim();
        if (value === "" || value === "0") {
          item.discount = 0;
        } else {
          item.discount = Math.max(0, Math.floor(Number(value) || 0));
        }
      }
    },
    removeProductFromCart(state, action) {
      const productId = action.payload;
      state.selectedProducts = state.selectedProducts.filter((item) => item.product.id !== productId);
    },
    clearCart(state) {
      state.selectedProducts = [];
    },
    setSelectedStore(state, action) {
      state.selectedStore = action.payload;
    },
    clearSelectedStore(state) {
      state.selectedStore = null;
    },
    incrementReceiptNumber(state) {
      state.lastReceiptNumber += 1;
    },
    resetReceiptNumber(state) {
      state.lastReceiptNumber = 0;
    },
    clearShift(state) {
      state.shift.current = null;
      state.shift.status = "idle";
      state.shift.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchProductsByBarcodePartial.pending, (state, action) => {
        state.searchStatus = "loading";
        state.searchError = null;
        state.lastSearchQuery = String(action.meta.arg?.barcodePartial || "").trim();
      })
      .addCase(searchProductsByBarcodePartial.fulfilled, (state, action) => {
        state.searchStatus = "succeeded";
        state.searchResults = action.payload || [];
      })
      .addCase(searchProductsByBarcodePartial.rejected, (state, action) => {
        state.searchStatus = "failed";
        const payload = action.payload;
        state.searchError =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to search products";
      })
      .addCase(fetchStores.pending, (state) => {
        state.stores.status = "loading";
        state.stores.error = null;
      })
      .addCase(fetchStores.fulfilled, (state, action) => {
        state.stores.status = "succeeded";
        state.stores.list = action.payload || [];
      })
      .addCase(fetchStores.rejected, (state, action) => {
        state.stores.status = "failed";
        const payload = action.payload;
        state.stores.error =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to fetch stores";
      })
      .addCase(createTransaction.pending, (state) => {
        state.transaction.status = "loading";
        state.transaction.error = null;
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.transaction.status = "succeeded";
        state.transaction.data = action.payload;
        state.selectedProducts = [];
        state.lastReceiptNumber += 1;
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.transaction.status = "failed";
        const payload = action.payload;
        state.transaction.error =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to create transaction";
      })
      .addCase(checkCurrentShift.pending, (state) => {
        state.shift.status = "loading";
        state.shift.error = null;
      })
      .addCase(checkCurrentShift.fulfilled, (state, action) => {
        state.shift.status = "succeeded";
        const shift = action.payload;
        
        if (shift && (!shift.store)) {
          state.shift.current = null;
        } else {
          state.shift.current = shift;
          if (shift?.store && !state.selectedStore) {
            if (typeof shift.store === "object") {
              state.selectedStore = shift.store;
            } else {
              const store = state.stores.list.find((s) => s.id === shift.store);
              if (store) {
                state.selectedStore = store;
              }
            }
          } else if (shift?.store && state.selectedStore) {
            const shiftStoreId = typeof shift.store === "object" ? shift.store.id : shift.store;
            const currentStoreId = typeof state.selectedStore === "object" ? state.selectedStore.id : state.selectedStore;
            if (shiftStoreId !== currentStoreId) {
              if (typeof shift.store === "object") {
                state.selectedStore = shift.store;
              } else {
                const store = state.stores.list.find((s) => s.id === shift.store);
                if (store) {
                  state.selectedStore = store;
                }
              }
            }
          }
        }
      })
      .addCase(checkCurrentShift.rejected, (state, action) => {
        state.shift.status = "failed";
        const payload = action.payload;
        state.shift.error =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to check shift";
      })
      .addCase(openShift.pending, (state) => {
        state.shift.status = "loading";
        state.shift.error = null;
      })
      .addCase(openShift.fulfilled, (state, action) => {
        state.shift.status = "succeeded";
        state.shift.current = action.payload;
        if (action.payload?.store) {
          if (typeof action.payload.store === "object") {
            state.selectedStore = action.payload.store;
          } else {
            const store = state.stores.list.find((s) => s.id === action.payload.store);
            if (store) {
              state.selectedStore = store;
            }
          }
        }
      })
      .addCase(openShift.rejected, (state, action) => {
        state.shift.status = "failed";
        const payload = action.payload;
        state.shift.error =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to open shift";
      })
      .addCase(closeShift.pending, (state) => {
        state.shift.status = "loading";
        state.shift.error = null;
      })
      .addCase(closeShift.fulfilled, (state) => {
        state.shift.status = "succeeded";
        state.shift.current = null;
      })
      .addCase(closeShift.rejected, (state, action) => {
        state.shift.status = "failed";
        const payload = action.payload;
        state.shift.error =
          (payload && typeof payload === "object" && (payload.detail || payload.message)) ||
          action.error?.message ||
          "Failed to close shift";
      });
  },
});

export const {
  clearSearchResults,
  addProductToCart,
  updateProductQuantity,
  incrementProductQuantity,
  decrementProductQuantity,
  updateProductDiscount,
  removeProductFromCart,
  clearCart,
  setSelectedStore,
  clearSelectedStore,
  incrementReceiptNumber,
  resetReceiptNumber,
  clearShift,
} = onlineSellerSlice.actions;

export const selectSearchResults = (state) => state.onlineSeller.searchResults || [];
export const selectSearchStatus = (state) => state.onlineSeller.searchStatus || "idle";
export const selectSearchError = (state) => state.onlineSeller.searchError || null;
export const selectLastSearchQuery = (state) => state.onlineSeller.lastSearchQuery || "";
export const selectSelectedProducts = (state) => state.onlineSeller.selectedProducts || [];
export const selectStores = (state) => state.onlineSeller.stores.list || [];
export const selectStoresStatus = (state) => state.onlineSeller.stores.status || "idle";
export const selectStoresError = (state) => state.onlineSeller.stores.error || null;
export const selectSelectedStore = (state) => state.onlineSeller.selectedStore || null;
export const selectTransactionStatus = (state) => state.onlineSeller.transaction.status || "idle";
export const selectTransactionError = (state) => state.onlineSeller.transaction.error || null;
export const selectTransactionData = (state) => state.onlineSeller.transaction.data || null;
export const selectLastReceiptNumber = (state) => state.onlineSeller.lastReceiptNumber || 0;
export const selectCurrentShift = (state) => state.onlineSeller.shift.current;
export const selectShiftStatus = (state) => state.onlineSeller.shift.status || "idle";
export const selectShiftError = (state) => state.onlineSeller.shift.error || null;

export const generateReceiptNumber = (receiptNumber) => {
  const num = Number(receiptNumber) || 0;
  return String(num).padStart(7, "0");
};

export default onlineSellerSlice.reducer;
